import type { Command, SimState } from '@/game/sim';
import { MAX_SHOT_TICKS } from '../rules/schema';
import { isMatchConfig } from '../sim/settings';
import { MAX_LOOKED, SCOUT_MAX_AVOID } from '../sim/systems/automation';
import { MAX_HIT_BY, MAX_REVEALED } from '../sim/systems/combat';
import { MAX_FOCUS_QUEUE } from '../sim/systems/pursuit';
import { MAX_TRAINING_QUEUE } from '../sim/training';
import { MAX_TARGET_TASKS, MAX_WORK_TARGET } from '../sim/workTargets';

/**
 * Validators for untrusted wire data.
 *
 * Pure module: no react/three/phop imports. phop's hosted simulation carries
 * commands and snapshots; these guards are handed to it so a malformed or
 * oversized payload never reaches the sim. Guards are structural (no zod).
 * Game rules (ownership, costs, placement) are still enforced by
 * applyCommands.
 */

/** Most unit ids one command may carry; bounds host work per input. */
const MAX_COMMAND_UNITS = 200;
/** Longest entity/type id accepted from the wire. */
const MAX_ID_LENGTH = 64;
/** Most shots a snapshot may carry (in flight or just landed); bounds combat work. */
const MAX_SHOTS = 5000;
/** Most a `Unit.pace` may be: a speed multiplier of 100, in thousandths (fixed.ts). */
const MAX_PACE = 100_000;
/** Highest building level a snapshot may be upgrading to (the rules have far fewer). */
const MAX_BUILDING_LEVEL = 99;
/** Most an `upgrading.progress` may be: an hour, in thousandths of a second. */
const MAX_UPGRADE_PROGRESS = 3_600_000;
const SHOT_KINDS: readonly unknown[] = ['tracer', 'arrow', 'shell', 'missile', 'beam'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_ID_LENGTH;
}

/** A stance a command may set (`Stance`). */
function isStance(value: unknown): boolean {
  return value === 'aggressive' || value === 'hold' || value === 'passive';
}

/** A `workTargets` map: a few task ids to non-negative integers (the sim clamps to the city's cap). */
function isWorkTargets(value: unknown): value is Record<string, number> {
  if (!isRecord(value) || Object.getPrototypeOf(value) !== Object.prototype) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= MAX_TARGET_TASKS &&
    entries.every(
      ([task, n]) =>
        isId(task) &&
        Number.isSafeInteger(n) &&
        (n as number) >= 0 &&
        (n as number) <= MAX_WORK_TARGET
    )
  );
}

function isIdList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.length <= MAX_COMMAND_UNITS &&
    value.every(isId)
  );
}

function isCell(value: unknown): value is { q: number; r: number } {
  return isRecord(value) && Number.isSafeInteger(value.q) && Number.isSafeInteger(value.r);
}

/** A well-formed sim Command (shape only — game rules are checked in the sim). */
function isCommand(value: unknown): value is Command {
  if (!isRecord(value)) return false;
  switch (value.type) {
    case 'move':
    case 'attackMove':
      return isIdList(value.unitIds) && isCell(value.target);
    case 'attack':
      return (
        isIdList(value.unitIds) &&
        isId(value.targetId) &&
        isCell(value.target) &&
        (value.queue === undefined || value.queue === true)
      );
    case 'unfocus':
      return isIdList(value.unitIds) && (value.targetId === undefined || isId(value.targetId));
    case 'spawn':
      return isId(value.cityId) && isId(value.unitType);
    case 'cancelTraining':
      return isId(value.cityId) && isId(value.trainingId);
    case 'place':
      return isId(value.buildingType) && isCell(value.target);
    case 'foundAt':
      return isId(value.unitId) && isCell(value.target);
    case 'watch':
      return isId(value.buildingId) && (value.target === null || isCell(value.target));
    case 'train':
      // One unit, or several (bounded like a selection).
      if (value.unitIds !== undefined) return isIdList(value.unitIds) && value.unitId === undefined;
      return isId(value.unitId);
    case 'foundCity':
    case 'blend':
    case 'expose':
      return isId(value.unitId);
    case 'setWorkFocus':
      // 'balanced' or a resource id (which the sim checks against its rules).
      return isId(value.cityId) && isId(value.focus);
    case 'setWorkTargets':
      return isId(value.cityId) && isWorkTargets(value.targets);
    case 'assign':
      // A tile, or a building's id (the sim checks what's there).
      return (
        isIdList(value.unitIds) &&
        (isCell(value.target) || isId(value.target)) &&
        (value.pin === undefined || typeof value.pin === 'boolean')
      );
    case 'setHome':
      return isIdList(value.unitIds) && isId(value.cityId);
    case 'upgrade':
    case 'cancel':
    case 'cancelUpgrade':
      return isId(value.buildingId);
    case 'promote':
      if (!isId(value.to)) return false;
      // A selection, or a city's best `count` units (bounded like a selection).
      if (value.unitIds !== undefined) return isIdList(value.unitIds) && value.cityId === undefined;
      return (
        isId(value.cityId) &&
        (value.from === undefined || isId(value.from)) &&
        (value.count === undefined ||
          value.count === 'all' ||
          (Number.isSafeInteger(value.count) &&
            (value.count as number) >= 1 &&
            (value.count as number) <= MAX_COMMAND_UNITS))
      );
    case 'automate':
    case 'group':
    case 'ungroup':
    case 'deploy':
      return isIdList(value.unitIds);
    case 'setStance':
      return isIdList(value.unitIds) && isStance(value.stance);
    case 'resign':
    case 'endMatch':
      return true;
    default:
      return false;
  }
}

/**
 * A whole number of thousandths ≥ 0 (sim/fixed.ts): hp, stock, loads, tile
 * resources, step progress and shot damage are kept as these.
 */
function isMilli(value: unknown): boolean {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

/** A city's stock (or plunder): resource id → thousandths. */
function isStock(value: unknown): boolean {
  return isRecord(value) && Object.values(value).every(isMilli);
}

/** A player's `lastPlunder`, read by the client's notification feed. */
function isPlunder(value: unknown): boolean {
  return (
    isRecord(value) &&
    Number.isFinite(value.tick) &&
    typeof value.from === 'string' &&
    isCell(value.pos) &&
    isStock(value.amounts)
  );
}

/** A player's `lastRaze`: a plunder record plus the building type and count. */
function isRaze(value: unknown): boolean {
  return (
    isPlunder(value) &&
    isRecord(value) &&
    typeof value.type === 'string' &&
    Number.isInteger(value.count) &&
    (value.count as number) > 0
  );
}

/** A unit's carried load, which the economy deposits into a city. */
function isCarry(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.res === 'string' &&
    isMilli(value.amount) &&
    (value.unloading === undefined || Number.isFinite(value.unloading))
  );
}

/**
 * A unit's optional building (`inside`) and group ids, which systems look up
 * by id. A snapshot from before `inside` replaced `stored` (a barracks id) is
 * migrated in place: the unit is inside that barracks.
 */
function isUnitLinks(u: Record<string, unknown>): boolean {
  if (u.stored !== undefined) {
    if (!isId(u.stored) || (u.inside !== undefined && u.inside !== u.stored)) return false;
    u.inside = u.stored;
    delete u.stored;
  }
  return (u.inside === undefined || isId(u.inside)) && (u.groupId === undefined || isId(u.groupId));
}

/** Ticks a unit has waited at a blocked step (movement re-routes after a while). */
function isBlocked(value: unknown): boolean {
  return value === undefined || (Number.isInteger(value) && (value as number) >= 0);
}

/** A unit's automation state: its phase picks what automation does with it. */
function isAuto(value: unknown, tick: number): boolean {
  return (
    value === undefined ||
    (isRecord(value) &&
      (value.phase === undefined ||
        value.phase === 'haul' ||
        value.phase === 'flee' ||
        value.phase === 'patrol') &&
      (value.target === undefined || isCell(value.target)) &&
      (value.work === undefined || isCell(value.work)) &&
      (value.retryTick === undefined || Number.isFinite(value.retryTick)) &&
      (value.blocked === undefined || value.blocked === true) &&
      (value.waitSince === undefined ||
        (Number.isSafeInteger(value.waitSince) &&
          (value.waitSince as number) >= 0 &&
          (value.waitSince as number) <= tick)) &&
      (value.dare === undefined || value.dare === true) &&
      (value.avoid === undefined ||
        (Array.isArray(value.avoid) &&
          value.avoid.length <= SCOUT_MAX_AVOID &&
          value.avoid.every(isCell))) &&
      (value.patrolSince === undefined ||
        (Number.isSafeInteger(value.patrolSince) &&
          (value.patrolSince as number) >= 0 &&
          (value.patrolSince as number) <= tick)))
  );
}

/**
 * A spy's disguise (the city it's blended into, looked up by id; spies.ts
 * drops it if it no longer holds), its exposure flag and its lurk count.
 */
function isSpyState(u: Record<string, unknown>): boolean {
  return (
    (u.disguise === undefined || (isRecord(u.disguise) && isId(u.disguise.cityId))) &&
    (u.exposed === undefined || u.exposed === true) &&
    (u.lurk === undefined || (Number.isSafeInteger(u.lurk) && (u.lurk as number) >= 0))
  );
}

/** A unit's order, which movement, pursuit and founding act on by kind. */
function isOrder(value: unknown): boolean {
  if (value === undefined) return true;
  if (!isRecord(value)) return false;
  switch (value.kind) {
    case 'move':
    case 'attackMove':
    case 'found':
      return isCell(value.target);
    case 'gather':
      // `pin`/`haul`: a pinned worker's job (assign); `retryTick`: its next search.
      return (
        isCell(value.target) &&
        (value.pin === undefined || value.pin === true) &&
        (value.haul === undefined || value.haul === true) &&
        (value.retryTick === undefined ||
          (Number.isSafeInteger(value.retryTick) && (value.retryTick as number) >= 0))
      );
    case 'build':
      return (
        isId(value.buildingId) &&
        (value.pin === undefined || value.pin === true) &&
        (value.retryTick === undefined ||
          (Number.isSafeInteger(value.retryTick) && (value.retryTick as number) >= 0))
      );
    case 'heal':
      return isId(value.targetId) && (value.post === undefined || isCell(value.post));
    case 'attack':
      return (
        isId(value.targetId) &&
        (value.guard === undefined || isId(value.guard)) &&
        (value.stuckAt === undefined || isId(value.stuckAt)) &&
        (value.retryAt === undefined || Number.isSafeInteger(value.retryAt)) &&
        (value.resume === undefined || isId(value.resume)) &&
        (value.onward === undefined || isCell(value.onward)) &&
        (value.queue === undefined ||
          (Array.isArray(value.queue) &&
            value.queue.length > 0 &&
            value.queue.length <= MAX_FOCUS_QUEUE &&
            value.queue.every(isId)))
      );
    default:
      return false;
  }
}

/**
 * A unit's `pace` (a speed multiplier in thousandths), which caps its speed:
 * positive, so it can't freeze a unit, and bounded.
 */
function isPace(value: unknown): boolean {
  return (
    value === undefined ||
    (Number.isSafeInteger(value) && (value as number) > 0 && (value as number) <= MAX_PACE)
  );
}

/** A unit's xp, which sets its rank and so its max hp and damage. */
function isXp(value: unknown): boolean {
  return value === undefined || (Number.isFinite(value) && (value as number) >= 0);
}

/** A ranged attacker's ticks until it fires again. */
function isReload(value: unknown): boolean {
  return (
    value === undefined ||
    (Number.isInteger(value) && (value as number) > 0 && (value as number) <= MAX_SHOT_TICKS)
  );
}

/** One shot of `SimState.shots`, whose damage combat applies at its impact tick. */
function isShot(value: unknown): boolean {
  return (
    isRecord(value) &&
    isId(value.id) &&
    isId(value.attacker) &&
    typeof value.owner === 'string' &&
    SHOT_KINDS.includes(value.kind) &&
    isId(value.targetId) &&
    isCell(value.from) &&
    isCell(value.to) &&
    Number.isSafeInteger(value.fireTick) &&
    Number.isSafeInteger(value.impactTick) &&
    (value.impactTick as number) > (value.fireTick as number) &&
    (value.impactTick as number) - (value.fireTick as number) <= MAX_SHOT_TICKS &&
    typeof value.hit === 'boolean' &&
    isMilli(value.damage)
  );
}

/** A unit's `hitBy`: enemy unit id → tick of its last hit, not ahead of the snapshot (bounded). */
function isHitBy(value: unknown, tick: number): boolean {
  if (value === undefined) return true;
  if (!isRecord(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= MAX_HIT_BY &&
    entries.every(
      ([id, at]) =>
        isId(id) && Number.isSafeInteger(at) && (at as number) >= 0 && (at as number) <= tick
    )
  );
}

const FOUND_FAILURE_REASONS: readonly unknown[] = [
  'unknown',
  'occupied',
  'too-close',
  'unreachable',
  'taken',
];

/** A player's `lastFoundFailure`: a dropped found order, for the client to report. */
function isFoundFailure(value: unknown, tick: number): boolean {
  return (
    value === undefined ||
    (isRecord(value) &&
      Number.isSafeInteger(value.tick) &&
      (value.tick as number) >= 0 &&
      (value.tick as number) <= tick &&
      isId(value.unitId) &&
      isCell(value.target) &&
      FOUND_FAILURE_REASONS.includes(value.reason))
  );
}

/** A player's `looked`: patrol spot → tick last in sight, not ahead of the snapshot (bounded). */
function isLooked(value: unknown, tick: number): boolean {
  if (value === undefined) return true;
  if (!isRecord(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= MAX_LOOKED &&
    entries.every(
      ([key, at]) =>
        /^-?\d+,-?\d+$/.test(key) &&
        Number.isSafeInteger(at) &&
        (at as number) >= 0 &&
        (at as number) <= tick
    )
  );
}

/** A player's `revealed`: attacker id → tick until which it's revealed (bounded). */
function isRevealed(value: unknown): boolean {
  if (value === undefined) return true;
  if (!isRecord(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length <= MAX_REVEALED &&
    entries.every(([id, until]) => isId(id) && Number.isSafeInteger(until))
  );
}

/** `SimState.shots`: a bounded list of shots. */
function isShots(value: unknown): boolean {
  return (
    value === undefined ||
    (Array.isArray(value) && value.length <= MAX_SHOTS && value.every(isShot))
  );
}

/** `SimState.groups`: group id → { leader: unit id, number: positive integer }. */
function isGroups(value: unknown): boolean {
  return (
    value === undefined ||
    (isRecord(value) &&
      Object.values(value).every(
        (g) =>
          isRecord(g) &&
          isId(g.leader) &&
          typeof g.number === 'number' &&
          Number.isInteger(g.number) &&
          g.number > 0
      ))
  );
}

/**
 * A city's training queue (`training`): bounded, each entry naming the
 * unit's id, type and training building, what was paid (refunded on
 * cancel) and its progress so far.
 */
function isTrainingQueue(value: unknown): boolean {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.length > 0 &&
      value.length <= MAX_TRAINING_QUEUE &&
      value.every(
        (t) =>
          isRecord(t) &&
          isId(t.id) &&
          isId(t.unitType) &&
          isId(t.at) &&
          isStock(t.paid) &&
          isMilli(t.progress)
      ))
  );
}

/**
 * A building's upgrade in progress (`upgrading`): the level it's heading for
 * (bounded), the progress so far and what was paid (refunded on cancel).
 */
function isUpgrading(value: unknown, b: Record<string, unknown>): boolean {
  return (
    value === undefined ||
    (isRecord(value) &&
      // Only a finished building upgrades, and only to the level after its own.
      b.buildProgress === 1 &&
      Number.isSafeInteger(value.level) &&
      (value.level as number) >= 2 &&
      (value.level as number) <= MAX_BUILDING_LEVEL &&
      value.level === ((b.level as number | undefined) ?? 1) + 1 &&
      isMilli(value.progress) &&
      (value.progress as number) <= MAX_UPGRADE_PROGRESS &&
      isStock(value.paid))
  );
}

/** A building's extra tiles (`footprint`), which placement, combat and occupancy index. */
function isFootprint(value: unknown): boolean {
  return value === undefined || (Array.isArray(value) && value.every(isCell));
}

/** A snapshot state with the fields tick() reads. */
function isSimState(value: unknown): value is SimState {
  if (!isRecord(value)) return false;
  if (typeof value.tick !== 'number') return false;
  // A snapshot with a bad status would silently freeze receivers (tick()
  // no-ops unless status === 'running'), so validate the fields tick() reads.
  if (value.status !== 'running' && value.status !== 'ended') return false;
  if (value.endedBy !== undefined && value.endedBy !== 'host') return false;
  if (typeof value.nextEntityId !== 'number') return false;
  if (!isRecord(value.players)) return false;
  const players = Object.values(value.players);
  if (
    !players.every(
      (p) =>
        isRecord(p) &&
        (p.lastPlunder === undefined || isPlunder(p.lastPlunder)) &&
        (p.lastRaze === undefined || isRaze(p.lastRaze)) &&
        (p.lostCity === undefined || p.lostCity === true) &&
        (p.resigned === undefined || p.resigned === true) &&
        (p.starvingSince === undefined || Number.isFinite(p.starvingSince)) &&
        (p.exposeCooldownUntil === undefined || Number.isSafeInteger(p.exposeCooldownUntil)) &&
        isRevealed(p.revealed) &&
        isLooked(p.looked, value.tick as number) &&
        isFoundFailure(p.lastFoundFailure, value.tick as number)
    )
  )
    return false;
  if (!isRecord(value.units)) return false;
  // A bad load would reach city stock when unloaded.
  if (
    !Object.values(value.units).every(
      (u) =>
        isRecord(u) &&
        isMilli(u.hp) &&
        isMilli(u.progress) &&
        (u.carry === undefined || isCarry(u.carry)) &&
        isUnitLinks(u) &&
        isXp(u.xp) &&
        isReload(u.reload) &&
        isHitBy(u.hitBy, value.tick as number) &&
        // Aggressive is the default and stays omitted.
        (u.stance === undefined || u.stance === 'hold' || u.stance === 'passive') &&
        isBlocked(u.blocked) &&
        isAuto(u.auto, value.tick as number) &&
        isOrder(u.order) &&
        isPace(u.pace) &&
        isSpyState(u)
    )
  )
    return false;
  // The match clock and map size every peer runs on.
  if (value.match !== undefined && !isMatchConfig(value.match)) return false;
  if (!isGroups(value.groups)) return false;
  if (!isShots(value.shots)) return false;
  if (!isRecord(value.explored)) return false;
  // Wild tiles' resources left, which gathering drains into loads.
  if (!isStock(value.tileResourceOverride)) return false;
  if (!isRecord(value.buildings)) return false;
  // Spending and scoring sum city stock, so a bad amount would poison them.
  return Object.values(value.buildings).every(
    (b) =>
      isRecord(b) &&
      isMilli(b.hp) &&
      (b.stock === undefined || isStock(b.stock)) &&
      isFootprint(b.footprint) &&
      isTrainingQueue(b.training) &&
      isUpgrading(b.upgrading, b) &&
      isReload(b.reload) &&
      (b.watch === undefined || isCell(b.watch)) &&
      (b.workFocus === undefined || isId(b.workFocus)) &&
      (b.workTargets === undefined || isWorkTargets(b.workTargets)) &&
      (b.lastStand === undefined || b.lastStand === true) &&
      (b.lastDamagedTick === undefined ||
        (Number.isSafeInteger(b.lastDamagedTick) &&
          (b.lastDamagedTick as number) >= 0 &&
          (b.lastDamagedTick as number) <= (value.tick as number)))
  );
}

export { isCommand, isRecord, isSimState, MAX_COMMAND_UNITS };
